import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial, DebugStep } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export class LandscapeFirstDebugStrategy implements CuttingStrategy {
    name = 'Orientation Priority (Debug)';
    debugSteps: DebugStep[] = [];
    visualizationCounter = 0;
    placedParts: IMaterial[] = [];
    portraitFirst: boolean = false; // Set to true for portrait-first strategy

    constructor(portraitFirst: boolean = false) {
        this.portraitFirst = portraitFirst;
        this.name = portraitFirst ? 'Portrait First (Debug)' : 'Landscape First (Debug)';
    }

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Clear debug steps
        this.debugSteps = [];
        this.visualizationCounter = 0;
        this.placedParts = [];

        // Sort parts by area (largest first)
        const sortedParts = [...parts].sort((a, b) => (b.width * b.height) - (a.width * a.height));
        const usedMaterials: IMaterial[] = [];
        const remainingParts = [...sortedParts];

        while (remainingParts.length > 0) {
            const currentMaterial: IMaterial = {
                ...material,
                id: `material-${usedMaterials.length + 1}`,
                children: [] as IMaterial[],
                debugSteps: [] as DebugStep[] // Store debug steps for this material
            };

            // Initialize with one large free rectangle
            const freeRectangles: Rectangle[] = [
                { x: 0, y: 0, width: material.width, height: material.height }
            ];

            // Add initial visualization of free rectangles
            this.addFreeRectanglesVisualization(freeRectangles, "Initial free rectangle");

            let placed = false;
            do {
                placed = false;

                // First pass: try to place parts in preferred orientation (if they're not square)
                for (let i = 0; i < remainingParts.length; i++) {
                    const part = remainingParts[i];

                    // Try preferred orientation first for non-square parts
                    let orientedPart = { ...part };
                    let rotated = false;

                    // Determine if we need to rotate based on portrait/landscape preference
                    const needsRotation = this.portraitFirst ?
                        (part.width > part.height) : // For portrait-first, rotate if width > height
                        (part.height > part.width);  // For landscape-first, rotate if height > width

                    // Apply rotation if needed
                    if (needsRotation) {
                        orientedPart = {
                            ...part,
                            width: part.height,
                            height: part.width
                        };
                        rotated = true;

                        // Add debug step for rotation
                        this.debugSteps.push({
                            type: 'rotate',
                            part: part,
                            reason: this.portraitFirst ? 'Rotating to portrait orientation' : 'Rotating to landscape orientation'
                        });
                    }

                    // Find best position
                    const position = this.findPositionWithDebug(orientedPart, freeRectangles);

                    if (position) {
                        // Place the part
                        const placedPart: IMaterial = {
                            ...orientedPart,
                            x: position.x,
                            y: position.y,
                            rotated
                        };

                        currentMaterial.children!.push(placedPart);

                        // Add debug step for placement
                        this.debugSteps.push({
                            type: 'place',
                            part: placedPart,
                            position: { x: position.x, y: position.y },
                            reason: rotated ?
                                `Placed in ${this.portraitFirst ? 'portrait' : 'landscape'} orientation` :
                                'Placed in original orientation'
                        });

                        // Keep track of placed parts
                        this.placedParts.push(placedPart);

                        // Update free rectangles
                        this.splitFreeRectangles(freeRectangles, position, placedPart);

                        // Remove part from remaining list
                        remainingParts.splice(i, 1);
                        placed = true;
                        break;
                    } else {
                        // Add debug step for failed placement
                        this.debugSteps.push({
                            type: 'reject',
                            part: orientedPart,
                            reason: 'Could not find suitable position in current orientation'
                        });

                        // If we tried in preferred orientation but failed, try in the alternate orientation
                        if (rotated) {
                            const alternatePosition = this.findPositionWithDebug(part, freeRectangles);

                            if (alternatePosition) {
                                // Place the part in its original orientation
                                const placedPart: IMaterial = {
                                    ...part,
                                    x: alternatePosition.x,
                                    y: alternatePosition.y,
                                    rotated: false
                                };

                                currentMaterial.children!.push(placedPart);

                                // Add debug step for placement
                                this.debugSteps.push({
                                    type: 'place',
                                    part: placedPart,
                                    position: { x: alternatePosition.x, y: alternatePosition.y },
                                    reason: this.portraitFirst ?
                                        'Placed in landscape orientation after portrait failed' :
                                        'Placed in portrait orientation after landscape failed'
                                });

                                // Keep track of placed parts
                                this.placedParts.push(placedPart);

                                // Update free rectangles
                                this.splitFreeRectangles(freeRectangles, alternatePosition, placedPart);

                                // Remove part from remaining list
                                remainingParts.splice(i, 1);
                                placed = true;
                                break;
                            } else {
                                // Add debug step for complete rejection
                                this.debugSteps.push({
                                    type: 'reject',
                                    part: part,
                                    reason: 'Could not fit in either orientation'
                                });
                            }
                        }
                    }
                }
            } while (placed);

            // Store debug steps for this material
            currentMaterial.debugSteps = [...this.debugSteps];

            // If we placed at least one part
            if (currentMaterial.children!.length > 0) {
                usedMaterials.push(currentMaterial);
            } else {
                // If we couldn't place any parts, break to avoid infinite loop
                break;
            }
        }

        return {
            usedMaterials,
            wastePercentage: this.calculateWaste(material, usedMaterials),
            strategyName: this.name,
            debugSteps: this.debugSteps // Include debug steps in the result
        };
    }

    private findPositionWithDebug(part: IMaterial, freeRectangles: Rectangle[]): Rectangle | null {
        let bestScore = Infinity;
        let bestPosition: Rectangle | null = null;

        for (const rect of freeRectangles) {
            // Check if part fits
            if (rect.width >= part.width && rect.height >= part.height) {
                // Add debug step for checking
                this.debugSteps.push({
                    type: 'check',
                    part: part,
                    position: { x: rect.x, y: rect.y },
                    reason: `Checking fit: ${part.width}x${part.height} in ${rect.width}x${rect.height}`
                });

                // Score using "best short side fit"
                const score = Math.min(
                    rect.width - part.width,
                    rect.height - part.height
                );

                if (score < bestScore) {
                    bestScore = score;
                    bestPosition = {
                        x: rect.x,
                        y: rect.y,
                        width: part.width,
                        height: part.height
                    };
                }
            }
        }

        return bestPosition;
    }

    private splitFreeRectangles(freeRectangles: Rectangle[], placed: Rectangle, placedPart?: IMaterial): void {
        // Create new rectangles based on the placement
        const newRectangles: Rectangle[] = [];

        for (let i = 0; i < freeRectangles.length; i++) {
            const rect = freeRectangles[i];

            // Skip if this free rectangle doesn't intersect with the placed rectangle
            if (rect.x >= placed.x + placed.width || rect.y >= placed.y + placed.height ||
                rect.x + rect.width <= placed.x || rect.y + rect.height <= placed.y) {
                continue;
            }

            // Right of the placed rectangle
            if (rect.x + rect.width > placed.x + placed.width) {
                newRectangles.push({
                    x: placed.x + placed.width,
                    y: rect.y,
                    width: rect.x + rect.width - (placed.x + placed.width),
                    height: rect.height
                });
            }

            // Below the placed rectangle
            if (rect.y + rect.height > placed.y + placed.height) {
                newRectangles.push({
                    x: rect.x,
                    y: placed.y + placed.height,
                    width: rect.width,
                    height: rect.y + rect.height - (placed.y + placed.height)
                });
            }

            // Mark this rectangle for removal
            freeRectangles[i] = { x: -1, y: -1, width: 0, height: 0 };
        }

        // Remove marked rectangles
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            if (freeRectangles[i].width === 0 || freeRectangles[i].height === 0) {
                freeRectangles.splice(i, 1);
            }
        }

        // Add new rectangles
        freeRectangles.push(...newRectangles);

        // Optimize rectangles by removing contained ones
        this.optimizeFreeRectangles(freeRectangles);

        // Visualize the free rectangles after splitting
        this.addFreeRectanglesVisualization(
            freeRectangles,
            `After placing part at (${placed.x},${placed.y}) of size ${placed.width}x${placed.height}`,
            placedPart
        );
    }

    private addFreeRectanglesVisualization(freeRectangles: Rectangle[], reason: string, placedPart?: IMaterial): void {
        const visualizationId = `freeRect-${++this.visualizationCounter}`;

        this.debugSteps.push({
            type: 'freeRectangles',
            reason,
            freeRectangles: [...freeRectangles], // Clone to prevent further modifications
            placedParts: [...this.placedParts], // Add all placed parts up to this point
            visualizationId
        } as DebugStep);
    }

    private optimizeFreeRectangles(freeRectangles: Rectangle[]): void {
        // Remove rectangles that are contained within others
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            const rect1 = freeRectangles[i];

            for (let j = 0; j < freeRectangles.length; j++) {
                if (i === j) continue;

                const rect2 = freeRectangles[j];

                if (rect2.x <= rect1.x && rect2.y <= rect1.y &&
                    rect2.x + rect2.width >= rect1.x + rect1.width &&
                    rect2.y + rect2.height >= rect1.y + rect1.height) {

                    freeRectangles.splice(i, 1);
                    break;
                }
            }
        }
    }

    private calculateWaste(originalMaterial: IMaterial, usedMaterials: IMaterial[]): number {
        const totalArea = originalMaterial.width * originalMaterial.height * usedMaterials.length;
        let usedArea = 0;

        usedMaterials.forEach(material => {
            if (material.children) {
                material.children.forEach(part => {
                    usedArea += part.width * part.height;
                });
            }
        });

        return ((totalArea - usedArea) / totalArea) * 100;
    }
}
